import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Slide,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import QRCode from "react-qr-code";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import TelegramIcon from "@mui/icons-material/Telegram";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TwitterIcon from "@mui/icons-material/Twitter";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useNavigate } from "react-router-dom";

const SlideUp = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));
SlideUp.displayName = "SlideUp";

const ShareOptionButton = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  userSelect: "none",
  transition: "transform 0.15s ease",
  "&:hover": { transform: "scale(1.08)" },
  "& .share-icon-circle": {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 24 },
  },
  "& .share-label": {
    fontSize: 11,
    color: "#444",
    textAlign: "center",
    fontFamily: "Inter, Arial, sans-serif",
    textTransform: "lowercase",
    lineHeight: 1.2,
  },
}));

const QRInfoCard = styled(Box)({
  background: "#F5EEFF",
  borderRadius: 12,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 8,
});

const LinkBox = styled(Box)({
  background: "#F5EEFF",
  borderRadius: 24,
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 16,
});

const SHARE_OPTIONS_CONFIG = [
  { id: "copy_url",    label: "copy url",    Icon: LinkIcon,              color: "#333",    iconColor: "#fff" },
  { id: "whatsapp",   label: "whatsapp",    Icon: WhatsAppIcon,          color: "#25D366", iconColor: "#fff" },
  { id: "instagram",  label: "direct",      Icon: InstagramIcon,         color: "#E1306C", iconColor: "#fff" },
  { id: "telegram",   label: "telegram",    Icon: TelegramIcon,          color: "#0088CC", iconColor: "#fff" },
  { id: "beemz_chat", label: "beemz chat",  Icon: ChatBubbleOutlineIcon, color: "#A259FF", iconColor: "#fff" },
  { id: "twitter",    label: "twitter",     Icon: TwitterIcon,           color: "#1DA1F2", iconColor: "#fff" },
  { id: "messages",   label: "messages",    Icon: SmsOutlinedIcon,       color: "#34C759", iconColor: "#fff" },
  { id: "snapchat",   label: "snapchat",    Icon: CameraAltOutlinedIcon, color: "#FFFC00", iconColor: "#333" },
];

/** Opens a share URL in a new tab */
const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer");

const ShareQRModal = ({ open, onClose, qr, onCopied }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const handleShareOption = useCallback(
    (optionId) => {
      if (!qr) return;
      const shareUrl = qr.qrValue || qr.url || "";
      const encoded = encodeURIComponent(shareUrl);
      const text = encodeURIComponent(`Check out ${qr.locationId?.businessLocationName || qr.locationName || "this location"} on Beemz: ${shareUrl}`);

      switch (optionId) {
        case "copy_url":
          navigator.clipboard
            ?.writeText(shareUrl)
            .then(() => onCopied?.())
            .catch(() => {});
          onClose();
          break;

        case "whatsapp":
          openShare(`https://wa.me/?text=${text}`);
          onClose();
          break;

        case "instagram":
          // Instagram has no web share URL — copy link as fallback
          navigator.clipboard?.writeText(shareUrl).then(() => onCopied?.()).catch(() => {});
          openShare("https://www.instagram.com/");
          onClose();
          break;

        case "telegram":
          openShare(`https://t.me/share/url?url=${encoded}&text=${encodeURIComponent(qr.locationId?.businessLocationName || qr.locationName || "Beemz Location")}`);
          onClose();
          break;

        case "beemz_chat":
          onClose();
          navigate("/chats");
          break;

        case "twitter":
          openShare(`https://twitter.com/intent/tweet?text=${text}`);
          onClose();
          break;

        case "messages":
          window.location.href = `sms:?body=${text}`;
          onClose();
          break;

        case "snapchat":
          openShare(`https://www.snapchat.com/scan?attachmentUrl=${encoded}`);
          onClose();
          break;

        default:
          break;
      }
    },
    [qr, onClose, onCopied, navigate]
  );

  if (!qr) return null;

  const locationName = qr.locationId?.businessLocationName || qr.locationName || "";
  const address = qr.locationId?.physicalAddress || qr.address || "";

  const modalContent = (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: "#E0E0E0",
            mx: "auto",
            display: { xs: "block", sm: "none" },
          }}
        />
        <Typography
          fontWeight={600}
          fontSize={16}
          sx={{
            fontFamily: "Inter, Arial, sans-serif",
            textTransform: "lowercase",
            flex: 1,
          }}
        >
          share location qr
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* QR Info Card */}
      <QRInfoCard>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 8,
            background: "#fff",
            border: "1px solid #E8E8E8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            p: 0.5,
          }}
        >
          <QRCode value={qr.qrValue || " "} size={38} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography fontWeight={700} fontSize={14} noWrap sx={{ fontFamily: "Inter, Arial, sans-serif" }}>
            {locationName}
          </Typography>
          {address && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <LocationOnOutlinedIcon sx={{ fontSize: 12, color: "#666" }} />
              <Typography fontSize={12} color="#666" noWrap sx={{ fontFamily: "Inter, Arial, sans-serif" }}>
                {address}
              </Typography>
            </Box>
          )}
        </Box>
      </QRInfoCard>

      {/* URL display */}
      <LinkBox>
        <LinkIcon sx={{ fontSize: 16, color: "#A259FF", flexShrink: 0 }} />
        <Typography fontSize={13} color="#A259FF" noWrap flex={1} sx={{ fontFamily: "Inter, Arial, sans-serif" }}>
          {qr.shortUrl || qr.qrValue}
        </Typography>
      </LinkBox>

      {/* Share options grid */}
      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={2}>
        {SHARE_OPTIONS_CONFIG.map((opt) => (
          <Tooltip key={opt.id} title={opt.label} placement="top">
            <ShareOptionButton
              onClick={() => handleShareOption(opt.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleShareOption(opt.id)}
            >
              <Box
                className="share-icon-circle"
                sx={{
                  backgroundColor: opt.color,
                  "& svg": { color: opt.iconColor },
                }}
              >
                <opt.Icon />
              </Box>
              <Typography className="share-label">{opt.label}</Typography>
            </ShareOptionButton>
          </Tooltip>
        ))}
      </Box>

      {/* Footer */}
      <Typography
        fontSize={12}
        color="#999"
        textAlign="center"
        sx={{ fontFamily: "Inter, Arial, sans-serif" }}
      >
        customers can scan the qr or open the link directly
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={isMobile ? SlideUp : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: isMobile
          ? {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              borderRadius: "16px 16px 0 0",
              maxHeight: "90vh",
            }
          : { borderRadius: 3, maxWidth: 400 },
      }}
    >
      <DialogContent sx={{ p: 0 }}>{modalContent}</DialogContent>
    </Dialog>
  );
};

export default ShareQRModal;
